/**
 * MER-478: map sync_log.error_detail strings to a one-line remediation
 * hint so a Failed entity row on /admin/customers/:slug shows what
 * broke AND whose move it is, instead of a bare red badge.
 *
 * Static pattern list for v1 per the ticket. Order matters: first
 * match wins, so put the most specific signatures first. Plain .mjs so
 * the node:test suite imports it directly (the fallbackWindow.mjs /
 * connectorHealth.mjs precedent).
 */

/**
 * Each class: id, regex tested against the raw error_detail, the
 * remediation line shown to staff, and an optional Linear ticket
 * reference (plain text id, per the v1 spec).
 */
export const ERROR_CLASSES = [
  {
    id: "unapplied_migration",
    // PostgREST schema-cache misses: PGRST204 (column not in schema
    // cache) and Postgres 42703 (undefined column) both mean prod is
    // missing a column the pipeline expects: a migration merged but
    // never applied (the MER-391 merge-gate family).
    pattern: /PGRST204|42703|column .* does not exist|schema cache/i,
    hint: "Unapplied migration: prod is missing a column the pipeline writes. Apply the pending migration on the droplet, then re-run the refresh.",
    ticketRef: "MER-391",
  },
  {
    id: "missing_table",
    // 42P01 undefined_table / PGRST205 table not in schema cache.
    pattern: /PGRST205|42P01|relation .* does not exist/i,
    hint: "Missing table: the entity's table has not been created in prod. Apply the pending migration before the next refresh.",
    ticketRef: "MER-391",
  },
  {
    id: "empty_source",
    pattern: /empty source|no rows|no input|source file missing|0 rows in source|empty dataframe/i,
    hint: "Source export missing or empty: check the upload portal for this week's export before re-running the refresh.",
  },
  {
    id: "permission_denied",
    // 42501 insufficient_privilege / RLS policy rejections.
    pattern: /42501|permission denied|row-level security|RLS/i,
    hint: "Permission denied: the sync key cannot write this table. Check the service key and RLS policies for the tenant.",
  },
  {
    id: "transient_network",
    pattern: /ECONNREFUSED|ECONNRESET|ETIMEDOUT|timeout|fetch failed|503|502|gateway/i,
    hint: "Transient network failure talking to Supabase. Usually self-heals on the next scheduled refresh; investigate only if it repeats.",
  },
  {
    id: "constraint_violation",
    pattern: /23505|duplicate key|23503|foreign key|23502|not[- ]null/i,
    hint: "Constraint violation: the source data conflicts with the table's keys. Inspect the offending rows in the source export before re-running.",
  },
];

/**
 * Classify a raw error_detail string. Returns the matching class
 * ({ id, hint, ticketRef? }) or null when nothing matches (the UI
 * then shows the raw error with no hint rather than a wrong hint).
 *
 * @param {string | null | undefined} errorDetail
 */
export function classifySyncError(errorDetail) {
  if (!errorDetail) return null;
  for (const cls of ERROR_CLASSES) {
    if (cls.pattern.test(errorDetail)) {
      return { id: cls.id, hint: cls.hint, ticketRef: cls.ticketRef ?? null };
    }
  }
  return null;
}
